import { computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { MerchantStatus } from 'app/helpers/app-constants';
import { ErrorService } from 'app/modules/error/service/error.service';
import { LoggedOnMerchantSummaryStore } from 'app/store/merchant.store';
import { LoggedOnUserPermissionsStore } from 'app/store/permissions.auth.store';
import { filter, map, of, switchMap, take } from 'rxjs';

export const landingRouteGuard: CanActivateFn = (route, state) => {
    const router = inject(Router);
    const errorHandler = inject(ErrorService);
    const permissionStore = inject(LoggedOnUserPermissionsStore);
    const navService = inject(NavigationService);
    const merchantStore = inject(LoggedOnMerchantSummaryStore);

    // Initialize the observable in the synchronous injection context
    const state$ = toObservable(
        computed(() => ({
            loaded: permissionStore.isLoaded(),
            hasMap: !!permissionStore.areNavigationsLoaded(),
            merchantLoaded: merchantStore.isLoaded(),
        }))
    );

    const init$ = !permissionStore.areNavigationsLoaded()
        ? navService.get()
        : of(null);

    return init$.pipe(
        // Switch to the already-initialized observable
        switchMap(() => state$),
        filter(
            ({ loaded, hasMap, merchantLoaded }) =>
                loaded && hasMap && merchantLoaded
        ),
        take(1),
        map(() => {
            if (
                state.url !== '/' &&
                state.url !== '/merchant' &&
                state.url !== '/colleague'
            ) {
                return true;
            }

            if (
                permissionStore.isAcceptedTermsAndConditions() &&
                merchantStore.isSingleMerchant()) {

                    let merchantStatus: string = merchantStore.merchants()[0].status;

                    switch (merchantStatus) {
                    case MerchantStatus.Incomplete:
                        return router.createUrlTree(['/merchant-wizard']);

                    case MerchantStatus.Inactive:
                        errorHandler.setError({
                        title: 'Page Not Found',
                        message: "The page you are looking for doesn't exist or has been removed.",
                        icon: 'mat_outline:arrow_back',
                        });

                    return router.createUrlTree(['/error']);

                    default:
                        break;
                    }

            }

            const landingPage = permissionStore.landingPage();

            if (!landingPage) {
                errorHandler.setError({
                    title: 'Access Denied',
                    message: "You don't have permission to view this page.",
                    icon: 'mat_outline:arrow_back',
                });

                return router.createUrlTree(['/error']);
            } else {
                return state.url === landingPage
                    ? true
                    : router.createUrlTree([landingPage]);
            }
        })
    );
};
